import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box } from '@chakra-ui/react'
import useStore from '../../../store'
import { colors } from '../../../utilities/colors'
import ReusableDrawer from '../../common/ReusableDrawer'
import Spinner from '../../common/Spinner'
import { NeutralButton, SolidGreenButton } from '../../common/Buttons'
import { WizardHeader } from '../../ui/wizard'
import PriceOptionCard from '../shared/PriceOptionCard'
import OrderLocationStep from './wizard/OrderLocationStep'
import { emptyOrderLocation } from '../../../constants/carpoolLocation'
import OrderReviewStep from './wizard/OrderReviewStep'
import OrderVehiclesStep from './wizard/OrderVehiclesStep'
import SaveLocationPrompt from './wizard/SaveLocationPrompt'
import AddressConfirmPrompt from './wizard/AddressConfirmPrompt'
import useTransportationBasePath from '../../../hooks/useTransportationBasePath'
import DrawerErrorBanner from '../shared/DrawerErrorBanner'

export default function CreateOrderPage({ isOpen, onClose, selectedPriceOption }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const basePath = useTransportationBasePath()
  const { carpoolQuote, createCarpoolOrder, dealer } = useStore()
  const [submitting, setSubmitting] = useState(false)
  const [validatingNext, setValidatingNext] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [origin, setOrigin] = useState({ ...emptyOrderLocation })
  const [destination, setDestination] = useState({ ...emptyOrderLocation })
  const [gatePasses, setGatePasses] = useState({})
  const [stepIndex, setStepIndex] = useState(0)
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null)
  const [savePromptStep, setSavePromptStep] = useState(null)
  const [askedSaveOrigin, setAskedSaveOrigin] = useState(false)
  const [askedSaveDestination, setAskedSaveDestination] = useState(false)
  const [addressPrompt, setAddressPrompt] = useState(null)
  const originStepRef = useRef(null)
  const destinationStepRef = useRef(null)
  const vehiclesStepRef = useRef(null)
  const originVerifiedRef = useMemo(() => ({ current: new Set() }), [])
  const destinationVerifiedRef = useMemo(() => ({ current: new Set() }), [])

  const steps = ['Origin', 'Destination', 'Vehicles', 'Review']
  const currentStep = steps[stepIndex]
  const isOriginStep = currentStep === 'Origin'
  const isDestinationStep = currentStep === 'Destination'
  const isVehiclesStep = currentStep === 'Vehicles'
  const isReviewStep = currentStep === 'Review'

  const quoteOrigin = carpoolQuote?.attributes?.origin
  const quoteDestination = carpoolQuote?.attributes?.destination
  const quoteCreatedBy = carpoolQuote?.attributes?.['created-by']
  const quoteDetails = carpoolQuote?.attributes?.['response-cache']
  const priceOptions = carpoolQuote?.attributes?.['price-options']
  const quoteOriginLocationType = carpoolQuote?.attributes?.['origin-location-type']
  const quoteDestinationLocationType = carpoolQuote?.attributes?.['destination-location-type']
  const priceOption = priceOptions?.find((option) => option.type === selectedPriceOption)

  const advanceStep = () => {
    setStepIndex((index) => Math.min(index + 1, steps.length - 1))
  }

  const afterLocationValidated = (which) => {
    if (which === 'origin' && !quoteOrigin && !askedSaveOrigin) {
      setSavePromptStep('origin')
      return
    }
    if (which === 'destination' && !quoteDestination && !askedSaveDestination) {
      setSavePromptStep('destination')
      return
    }
    advanceStep()
  }

  const runLocationStep = async (which, ref) => {
    const result = await ref.current?.validate()
    if (!result || result.reason === 'invalid_fields') return
    if (result.ok) {
      afterLocationValidated(which)
      return
    }
    if (result.reason === 'unverified') {
      setAddressPrompt({ which, suggestion: result.suggestion })
    }
  }

  const handleNext = async () => {
    setValidatingNext(true)
    try {
      if (isOriginStep) {
        await runLocationStep('origin', originStepRef)
        return
      }
      if (isDestinationStep) {
        await runLocationStep('destination', destinationStepRef)
        return
      }
      if (isVehiclesStep) {
        if (!(await vehiclesStepRef.current?.isValid())) return
        advanceStep()
      }
    } finally {
      setValidatingNext(false)
    }
  }

  const handleAddressPromptUseSuggested = () => {
    if (!addressPrompt) return
    const { which, suggestion } = addressPrompt
    const setter = which === 'origin' ? setOrigin : setDestination
    const stepRef = which === 'origin' ? originStepRef : destinationStepRef
    const updated = (prev) => {
      const next = { ...prev, address: suggestion.address, address_2: suggestion.address_2 || prev.address_2 }
      stepRef.current?.markVerified(next)
      return next
    }
    setter(updated)
    setAddressPrompt(null)
    afterLocationValidated(which)
  }

  const handleAddressPromptUseEntered = () => {
    if (!addressPrompt) return
    const { which } = addressPrompt
    const stepRef = which === 'origin' ? originStepRef : destinationStepRef
    stepRef.current?.markVerified()
    setAddressPrompt(null)
    afterLocationValidated(which)
  }

  const handleAddressPromptEdit = () => setAddressPrompt(null)

  const handleSavePromptComplete = () => {
    if (savePromptStep === 'origin') setAskedSaveOrigin(true)
    else if (savePromptStep === 'destination') setAskedSaveDestination(true)
    setSavePromptStep(null)
    advanceStep()
  }

  const handleBack = () => {
    setStepIndex((index) => Math.max(index - 1, 0))
  }

  const handleSubmit = async () => {
    if (!termsAcceptedAt) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      const vehiclesPayload =
        Object.keys(gatePasses).length > 0
          ? quoteDetails.vehicles.map((vehicle) => ({ gate_pass_base64: gatePasses[vehicle.vin]?.base64 || null }))
          : null

      const payload = {
        carpool_order: {
          price_option: selectedPriceOption,
          origin: {
            ...origin,
            city: quoteDetails.origin.city,
            state: quoteDetails.origin.state,
            postal_code: quoteDetails.origin.postal_code,
            type: quoteOriginLocationType,
          },
          destination: {
            ...destination,
            city: quoteDetails.destination.city,
            state: quoteDetails.destination.state,
            postal_code: quoteDetails.destination.postal_code,
            type: quoteDestinationLocationType,
          },
          carpool_terms_accepted_at: termsAcceptedAt,
          ...(vehiclesPayload ? { vehicles: vehiclesPayload } : {}),
        },
        dealer_id: dealer.id,
        carpool_quote_id: carpoolQuote.attributes.id,
      }
      await createCarpoolOrder(payload)
      navigate(`${basePath}/orders`)
    } catch (error) {
      setSubmitError(error?.message || t('Error creating carpool order.'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    setSubmitError(null)
    onClose?.()
  }

  const renderBody = () => {
    if (submitting) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" w="full" py={10}>
          <Spinner />
        </Box>
      )
    }
    if (!quoteDetails) return null
    return (
      <Box display="flex" flexDirection="column" gap={4} w="full" minW={0} py={2} px={{ base: 2, md: 0 }}>
        <DrawerErrorBanner message={submitError} onDismiss={() => setSubmitError(null)} />
        <WizardHeader
          title={t(currentStep)}
          progress={((stepIndex + 1) / steps.length) * 100}
          showBackButton={false}
        />
        {priceOption && (
          <PriceOptionCard
            option={priceOption}
            horizontalOrientation
            vehicleCount={quoteDetails?.vehicles?.length || 1}
          />
        )}

        {isOriginStep && (
          <OrderLocationStep
            stepRef={originStepRef}
            label={t('Origin')}
            apiLocation={quoteOrigin}
            quoteLocation={quoteDetails.origin}
            locationType={quoteOriginLocationType}
            value={origin}
            onChange={setOrigin}
            accentColor={colors.yellow}
            createdBy={quoteCreatedBy}
            verifiedRef={originVerifiedRef}
            notesLabel={t('Pickup Instructions')}
          />
        )}

        {isDestinationStep && (
          <OrderLocationStep
            stepRef={destinationStepRef}
            label={t('Destination')}
            apiLocation={quoteDestination}
            quoteLocation={quoteDetails.destination}
            locationType={quoteDestinationLocationType}
            value={destination}
            onChange={setDestination}
            accentColor={'#4A90D9'}
            createdBy={quoteCreatedBy}
            verifiedRef={destinationVerifiedRef}
            notesLabel={t('Delivery Instructions')}
          />
        )}

        {isVehiclesStep && quoteDetails && (
          <OrderVehiclesStep
            stepRef={vehiclesStepRef}
            vehicles={quoteDetails.vehicles || []}
            gatePasses={gatePasses}
            setGatePasses={setGatePasses}
          />
        )}

        {isReviewStep && (
          <OrderReviewStep
            origin={origin}
            destination={destination}
            quoteDetails={quoteDetails}
            originLocationType={quoteOriginLocationType}
            destinationLocationType={quoteDestinationLocationType}
            vehicles={quoteDetails?.vehicles || []}
            gatePasses={gatePasses}
            termsAcceptedAt={termsAcceptedAt}
            onTermsAcceptedChange={setTermsAcceptedAt}
          />
        )}
      </Box>
    )
  }

  const footer = (!priceOption || submitting) ? null : (
    <Box display="flex" gap={2} my={4} justifyContent="center" w="full">
      {stepIndex > 0 && <NeutralButton text={t('Back')} onClick={handleBack} />}
      {!isReviewStep && (
        <NeutralButton
          text={t('Next')}
          onClick={handleNext}
          loading={validatingNext}
          disabled={validatingNext}
        />
      )}
      {isReviewStep && (
        <SolidGreenButton
          text={t('Place Order')}
          onClick={handleSubmit}
          disabled={submitting || !termsAcceptedAt}
          loading={submitting}
        />
      )}
      <NeutralButton text={t('Cancel')} onClick={handleClose} />
    </Box>
  )

  return (
    <>
      <ReusableDrawer
        isOpen={isOpen}
        onClose={handleClose}
        placement="bottom"
        title={t('Create Order')}
        bgColor={colors.grey900}
        size={{ base: 'full', md: 'lg' }}
        titleStyle="flex items-center justify-center w-full"
        bodyStyle="flex flex-col items-center justify-start overflow-y-auto"
        footerStyle="flex items-center justify-center w-full"
        bodyContent={renderBody()}
        footerContent={footer}
      />
      <AddressConfirmPrompt
        isOpen={!!addressPrompt}
        label={addressPrompt?.which === 'origin' ? t('Origin') : t('Destination')}
        enteredLocation={addressPrompt?.which === 'origin' ? origin : destination}
        quoteLocation={addressPrompt?.which === 'origin' ? quoteDetails?.origin : quoteDetails?.destination}
        suggestion={addressPrompt?.suggestion || null}
        onUseSuggested={handleAddressPromptUseSuggested}
        onUseEntered={handleAddressPromptUseEntered}
        onEdit={handleAddressPromptEdit}
      />
      <SaveLocationPrompt
        isOpen={!!savePromptStep}
        label={savePromptStep === 'origin' ? t('Origin') : t('Destination')}
        location={savePromptStep === 'origin' ? origin : destination}
        quoteLocation={savePromptStep === 'origin' ? quoteDetails?.origin : quoteDetails?.destination}
        locationType={savePromptStep === 'origin' ? quoteOriginLocationType : quoteDestinationLocationType}
        onComplete={handleSavePromptComplete}
      />
    </>
  )
}
